import * as React from "react";
import { Link } from "react-router-dom";
import { TrendingDown, TrendingUp } from "lucide-react";
import { cn } from "@/lib/utils";

// Stat tile per the dashboard/reports mocks: a tinted icon square with the
// uppercase label beside it, the big display number below, then a caption or
// a vs-previous-period trend line.

export type StatTone = "default" | "accent" | "warn" | "ok" | "purple" | "orange" | "danger";

const valueTone: Record<StatTone, string> = {
  default: "text-text",
  accent: "text-brand-on",
  warn: "text-warn-on",
  ok: "text-success-on",
  purple: "text-purple-on",
  orange: "text-orange-on",
  danger: "text-danger-on",
};

const iconTile: Record<StatTone, string> = {
  default: "bg-surface-2 text-text-2",
  accent: "bg-brand-tint text-brand-on",
  warn: "bg-warn-tint text-warn-on",
  ok: "bg-success-tint text-success-on",
  purple: "bg-purple-tint text-purple-on",
  orange: "bg-orange-tint text-orange-on",
  danger: "bg-danger-tint text-danger-on",
};

export interface StatCardProps extends React.HTMLAttributes<HTMLDivElement> {
  label: React.ReactNode;
  value: React.ReactNode;
  caption?: React.ReactNode;
  icon?: React.ReactNode;
  /** Colors the icon tile; `valueToned` extends it to the number itself. */
  tone?: StatTone;
  valueToned?: boolean;
  /** Percent change vs the previous period, e.g. { pct: 13, label: "vs Jun 1–30" }. */
  trend?: { pct: number; label: string } | null;
  to?: string;
}

export const StatCard = React.forwardRef<HTMLDivElement, StatCardProps>(
  (
    { label, value, caption, icon, tone = "default", valueToned = true, trend, to, className, ...props },
    ref,
  ) => {
    const body = (
      <>
        <div className="flex items-center gap-3">
          {icon ? (
            <span
              className={cn(
                "flex h-10 w-10 shrink-0 items-center justify-center rounded-lg",
                iconTile[tone],
              )}
            >
              {icon}
            </span>
          ) : null}
          <p className="text-[11px] font-medium uppercase tracking-[0.14em] text-text-2">{label}</p>
        </div>
        <p
          className={cn(
            "mt-3 font-display text-4xl font-bold leading-none tracking-tight tabular-nums",
            valueToned ? valueTone[tone] : "text-text",
          )}
        >
          {value}
        </p>
        {trend ? (
          <p
            className={cn(
              "mt-2 flex items-center gap-1 text-xs font-medium",
              trend.pct < 0 ? "text-danger-on" : "text-success-on",
            )}
          >
            {trend.pct < 0 ? (
              <TrendingDown size={13} strokeWidth={2} aria-hidden />
            ) : (
              <TrendingUp size={13} strokeWidth={2} aria-hidden />
            )}
            {Math.abs(trend.pct)}%
            <span className="font-normal text-text-2">{trend.label}</span>
          </p>
        ) : caption ? (
          <p className="mt-2 text-xs text-text-2">{caption}</p>
        ) : null}
      </>
    );

    const surface = "rounded-lg border border-border bg-surface px-5 py-4 shadow-subtle";

    if (typeof to === "string") {
      return (
        <Link
          to={to}
          className={cn(
            surface,
            "block no-underline transition-[transform,box-shadow,border-color] duration-150 ease-out",
            "hover:-translate-y-px hover:border-brand-400 hover:shadow-raised",
            className,
          )}
        >
          {body}
        </Link>
      );
    }

    return (
      <div ref={ref} className={cn(surface, className)} {...props}>
        {body}
      </div>
    );
  },
);
StatCard.displayName = "StatCard";
